import { DEFAULT_STATUS_LEVEL } from '@/common/components/molecules/hexagonChart/hexagonChart.define';
import { type HeatmapChartOption } from '@/dashboard/features/widgets/heatmap/domain/heatmapSetting.types';
import { getYesterdayDate } from '@/dashboard/features/widgets/oneDayCompare/widget/utils/compareDate.utils';
import {
  ExclusiveChartOption,
  StatusHexaOption,
  StatusSummaryOption,
  TopologyWidgetChartOption,
} from '@/dashboard/features/widgetSettings/editModeWidgetSettings/components/architecture/types/architectureWidgetSettings.types';
import { OneDayCompareChartOption } from '@/dashboard/features/widgets/oneDayCompare/settings/types/oneDayCompareSettings.types';
import { EqualizerChartOption } from '@/dashboard/features/widgets/equalizer/settings/components/equalizerSettingsArticle.setup';
import { GaugeChartOption } from '@/dashboard/features/widgets/gauge/settings/components/gaugeSettingArticle.setup';
import { TimeSeriesChartOption } from '@/dashboard/features/widgetSettings/editModeWidgetSettings/components/graphs/types/graphWidgetSettings.types';
import {
  PieChartOption,
  DEFAULT_PIE_CHART_OPTION,
} from '@/dashboard/features/widgets/pie/settings/domain/pieWidgetChartOption';
import { DEFAULT_STATUS_SUMMARY_CHART_OPTION } from '@/dashboard/features/widgets/statusSummary/settings/domain/statusSummaryChartOption';
import { ScatterChartOption } from '@/dashboard/features/widgets/scatter/settings/scatterSettingsArticle.setup';
import { ScatterDotSize } from '@/dashboard/features/widgets/scatter/settings/scatterSettingsChartOption/dotSizeList/dotSizeList.types';
import { ScoreboardChartOption } from '@/dashboard/features/widgets/scoreboard/settings/types/scoreboardWidgetOption.types';
import { StackedProportionBarChartOption } from '@/dashboard/features/widgets/stackedProportionBar/settings/components/stackedProportionBarSettingsArticle.setup';
import { ImageWidgetOption } from '@/dashboard/features/widgets/image/settings/imageWidgetSettings.setup';
import type { TopListChartOption } from '@/dashboard/features/widgets/topList/widget/types/topList.types';
import { MemoWidgetChartOption } from '@/dashboard/features/widgets/memo/settings/memoWidgetSettings.setup';
import { TreeGridChartOption } from '@/dashboard/features/widgets/treeGrid/settings/domain/treeGridSettings.types';
import { COLOR_PALETTE_LIST } from './color.defines';
import { WidgetChartType } from '@/dashboard/shared/types/widgets.types';
import { BusinessChartOption } from '@/dashboard/features/widgets/business/settings/domain/businessSettings.types';
import { ServerChartOption } from '@/dashboard/features/widgets/server/settings/domain/types';
import {
  DEFAULT_MEMORY_STAT_VALUE,
  TRX_ITEMS,
} from '@/dashboard/features/widgets/server/settings/domain/constants';
import type { AlertChartOption } from '@/dashboard/features/widgets/alert/settings/domain/alertChartOption';
import { DEFAULT_ALERT_CHART_OPTION } from '@/dashboard/features/widgets/alert/settings/domain/alertChartOption.constants';
import { DEFAULT_TREE_GRID_CHART_OPTION } from '@/dashboard/features/widgets/treeGrid/settings/domain/treeGridSettings.constants';
import {
  DEFAULT_CHART_DATA,
  DEFAULT_TOP_LIST_SORT_TYPE,
} from '../../../../shared/constants/chartData.constants';

export type WidgetChartOptionsMap = {
  // graph
  TIME_SERIES: TimeSeriesChartOption;
  SCATTER: ScatterChartOption;
  ONE_DAY_COMPARE: OneDayCompareChartOption;
  EQUALIZER: EqualizerChartOption;
  SCOREBOARD: ScoreboardChartOption;
  PIE: PieChartOption;
  STACKED_PROPORTION_BAR: StackedProportionBarChartOption;
  GAUGE: GaugeChartOption;
  ACTION_VIEW: undefined;
  HEATMAP: HeatmapChartOption;
  // list
  TABLE: TreeGridChartOption;
  TREE_GRID: TreeGridChartOption;
  TOP_LIST: TopListChartOption;
  // architecture
  EXCLUSIVE: ExclusiveChartOption;
  STATUS: StatusHexaOption;
  STATUS_SUMMARY: StatusSummaryOption;
  TOPOLOGY: TopologyWidgetChartOption;
  SERVER: ServerChartOption;
  // business
  SERVICE_CARD: BusinessChartOption;
  // etc
  MEMO: MemoWidgetChartOption;
  TEXT: undefined;
  IMAGE: ImageWidgetOption;
  HORIZONTAL_FILTER: undefined;
  VERTICAL_FILTER: undefined;
  GROUP: undefined;
  LEGEND: undefined;
  TAB_GROUP: undefined;
  CLUSTER: undefined;
  REALTIME: AlertChartOption;
  BY_PERIOD: AlertChartOption;
};

export type ChartOptions<T extends WidgetChartType> = T extends keyof WidgetChartOptionsMap
  ? WidgetChartOptionsMap[T]
  : never;

export const DEFAULT_THRESHOLD_COLOR = {
  background: 'var(--color-gray-07-01)',
  font: 'var(--color-gray-00-12)',
};

export const DefaultChartOptions: { [K in WidgetChartType]: ChartOptions<K> } = {
  TIME_SERIES: {
    showLegend: false,
    useMarkers: false,
    showMaxValue: false,
    markerByMetrics: {
      use: false,
      dataId: '',
      category: '',
      targets: [],
      seriesType: 'avg',
      showCurrentValue: false,
    },
    interpolation: undefined,
    referenceInfo: undefined,
    decimalOptions: {
      use: false,
      decimalsPrecision: 3,
    },
    thresholdsOptions: undefined,
    useCountUnit: true,
    settingsMode: 'advanced',
    queryStatTimezone: {
      mode: 'account',
      timezone: undefined,
    },
  },
  SCATTER: {
    yAxisInfo: {
      autoScale: true,
      min: 0,
      max: 10,
    },
    dotSize: ScatterDotSize[1],
    showLegend: false,
    useCountUnit: true,
    autoScaleMax: undefined,
    referenceInfo: undefined,
    decimalOptions: {
      use: false,
      decimalsPrecision: 3,
    },
    queryStatTimezone: {
      mode: 'account',
      timezone: undefined,
    },
  },
  ONE_DAY_COMPARE: {
    showLegend: true,
    compareDate: {
      item: 'yesterday',
      date: getYesterdayDate(new Date()).getTime(),
      isLastFixedDate: false,
      lastSelectedDate: 'firstDay',
      lastDaysOfTheWeek: 'monday',
      lastValue: 1,
      lastFixedValue: 1,
      thisSelectedDate: 'firstDay',
      thisDaysOfTheWeek: 'monday',
      thisValue: 1,
      lastUnit: 'day',
      thisUnit: 'week',
    },
    referenceInfo: undefined,
    decimalOptions: {
      use: false,
      decimalsPrecision: 3,
    },
    settingsMode: 'advanced',
    useCountUnit: true,
  },
  EQUALIZER: {
    tableData: [],
    showLegend: false,
    yAxisOption: {
      mode: 'auto',
      useAutoMin: false,
      minValue: undefined,
      fixedValue: undefined,
    },
    decimalOptions: {
      use: false,
      decimalsPrecision: 3,
    },
    sortByItems: [],
    queryStatTimezone: {
      mode: 'account',
      timezone: undefined,
    },
  },
  SCOREBOARD: {
    showLegend: false,
    useCountUnit: true,
    decimalOptions: {
      use: false,
      decimalsPrecision: 3,
    },
    display1: {
      chartOption: {
        marker: {
          use: false,
          hideStatName: false,
          valueFormat: {
            use: false,
            format: '',
          },
        },
        formatting: {
          title: {
            textSize: 14,
            isAuto: true,
            horizontalAlign: 'left',
          },
          value: {
            textSize: 40,
            isAuto: true,
            verticalAlign: 'bottom',
            horizontalAlign: 'right',
          },
        },
        layoutOptions: {
          use: false,
          width: null,
          height: null,
        },
      },
      displayRules: [
        {
          use: false,
          threshold: {
            type: 'absolute',
            unit: '',
            absolute: {
              values: [
                {
                  start: null,
                  end: null,
                  label: null,
                  backgroundColor: DEFAULT_THRESHOLD_COLOR.background,
                  fontColor: DEFAULT_THRESHOLD_COLOR.font,
                  isError: true,
                },
              ],
            },
            proportion: {
              min: {
                value: null,
                isError: true,
              },
              max: {
                value: null,
                isError: true,
              },
              values: [
                {
                  start: null,
                  end: null,
                  label: null,
                  backgroundColor: DEFAULT_THRESHOLD_COLOR.background,
                  fontColor: DEFAULT_THRESHOLD_COLOR.font,
                  isError: true,
                },
              ],
            },
          },
        },
      ],
    },
    queryStatTimezone: {
      mode: 'account',
      timezone: undefined,
    },
  },
  PIE: DEFAULT_PIE_CHART_OPTION,
  STACKED_PROPORTION_BAR: {
    showLegend: false,
    decimalOptions: {
      use: false,
      decimalsPrecision: 3,
    },
    useCountUnit: true,
    barOrientation: 'horizontal',
    barSegment: 'target',
    axisSetting: {
      use: false,
      auto: true,
      min: 40,
    },
    queryStatTimezone: {
      mode: 'account',
      timezone: undefined,
    },
  },
  GAUGE: {
    showLegend: false,
    useCountUnit: true,
    multiGauge: undefined,
    marker: undefined,
    gaugeSetting: undefined,
    decimalOptions: {
      use: false,
      decimalsPrecision: 3,
    },
  },
  ACTION_VIEW: undefined,
  HEATMAP: {
    yAxis: {
      type: 'auto',
      min: 0,
      max: 0,
    },
    thresholds: [],
    useCellPadding: false,
    showLegend: true,
    decimalOptions: {
      use: false,
      decimalsPrecision: 3,
    },
    formulas: [],
  },
  TABLE: {
    limit: 50,
    // sortType: DEFAULT_ALERT_CHART_DATA_VALUES.ORDER_BY,
    // sortByColumn: '',
    // isShowHeader: false,
    // columns: [],
    // rowColors: [],
    // columnList: undefined,
    // referenceInfo: undefined,
    sortType: DEFAULT_CHART_DATA.ORDER_BY,
    isShowHeader: false,
    columns: [],
    sortColumn: '',
    groupByColumn: 'none',
    groupChild: {
      sortColumn: '',
      sortType: DEFAULT_CHART_DATA.ORDER_BY,
    },
    tree: {
      parent: {
        id: '',
        sortColumn: '',
        sortType: DEFAULT_CHART_DATA.ORDER_BY,
      },
      child: {
        id: '',
        sortColumn: '',
        sortType: DEFAULT_CHART_DATA.ORDER_BY,
      },
    },
    isTreeMode: true,
    rowColors: [],
    rawColumns: undefined,
    referenceInfo: undefined,
    tokenFilter: {
      use: true,
    },
    queryStatTimezone: {
      mode: 'account',
      timezone: undefined,
    },
  },
  TREE_GRID: DEFAULT_TREE_GRID_CHART_OPTION,
  TOP_LIST: {
    limit: 50,
    sortType: DEFAULT_TOP_LIST_SORT_TYPE,
    sortColumn: '',
    displayStat: '',
    statItems: [],
    referenceInfo: undefined,
    useCountUnit: true,
    queryStatTimezone: {
      mode: 'account',
      timezone: undefined,
    },
  },
  EXCLUSIVE: {
    customColors: [],
    sortCriteria: 'targetName',
    orderBy: DEFAULT_CHART_DATA.ORDER_BY,
  },
  STATUS: {
    groupSetting: {
      use: false,
      groupKey: undefined,
      orderBy: DEFAULT_CHART_DATA.ORDER_BY,
    },
    statusLevel: DEFAULT_STATUS_LEVEL,
    statusSettings: [],
    markerStat: null,
    legend: true,
    tileMinSize: 'large',
  },
  STATUS_SUMMARY: DEFAULT_STATUS_SUMMARY_CHART_OPTION,
  TOPOLOGY: {
    backgroundImage: 'worldMap',
    backgroundImageFile: {
      imageId: '',
      mimeType: '',
      type: '',
      originImageName: '',
      originImage: '',
    },
    nodeOptions: [],
    edgeOption: {
      use: true,
      statInfo: {
        stat: undefined,
        aggregation: 'sum',
        color: 'var(--color-gray-03-09)',
      },
      labelDisplayType: 'none',
      threshold: {
        use: false,
        hasValueError: false,
        items: [],
        warning: {
          // TODO: topologyMain 로직 변경 후 삭제 예정
          value: 0,
          operator: '>',
          color: COLOR_PALETTE_LIST.YELLOW[1].background,
        },
        critical: {
          // TODO: topologyMain 로직 변경 후 삭제 예정
          value: 0,
          operator: '>',
          color: COLOR_PALETTE_LIST.ORANGE[3].background,
        },
      },
      thresholdLegend: {
        displayType: 'none',
        options: {
          order: 'normal',
          fontSize: 12,
          isAutoSize: false,
          horizontalAlign: 'right',
          verticalAlign: 'bottom',
        },
      },
    },
    groupingOption: {
      use: false,
      type: 'targetType',
    },
  },
  SERVER: {
    useLegend: true,
    maxTrx: TRX_ITEMS[1].value,
    memoryStat: DEFAULT_MEMORY_STAT_VALUE,
    statDataList: [],
  },
  SERVICE_CARD: {},
  MEMO: {
    content: '',
    htmlContent: '',
  },
  TEXT: undefined,
  IMAGE: {
    imgSrc: null,
    fileName: '',
  },
  HORIZONTAL_FILTER: undefined,
  VERTICAL_FILTER: undefined,
  GROUP: undefined,
  LEGEND: undefined,
  TAB_GROUP: undefined,
  CLUSTER: undefined,
  REALTIME: DEFAULT_ALERT_CHART_OPTION,
  BY_PERIOD: DEFAULT_ALERT_CHART_OPTION,
};
